import { useState } from "react";
import { Phone, Mail, MapPin, CalendarDays, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Elements";
import { PHONE_DISPLAY, TEL_HREF, EMAIL, MAILTO, CALENDLY } from "@/data/content";

const INTERESTS = ["Buying", "Refinancing", "Selling (FSBO)", "Joining the Team"];

const API = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", interest: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setSent(true);
    } catch (err) {
      setError(
        "We couldn't submit your message just now. Please call Joe at " +
          PHONE_DISPLAY +
          " or email directly."
      );
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-navy placeholder-ink/40 outline-none transition-colors focus:border-gold";
  const labelCls = "mb-1.5 block text-[11px] font-medium uppercase tracking-[0.12em] text-ink";

  return (
    <div>
      <Seo
        title="Contact Joe Spellman | Family First Mortgage USA | (863) 559-8288"
        description="Contact Joe Spellman at Family First Mortgage USA — call (863) 559-8288, email jspellman@nexalending.com, or book a free consultation. 6185 Deltona Blvd, Spring Hill, FL 34606."
      />
      <PageHeader line1="Let's Talk About" line2="Your Home Loan." />

      <section className="bg-cream py-20 md:py-24" data-testid="contact-body">
        <div className="wrap grid gap-10 lg:grid-cols-[1fr_1.25fr]">
          <Reveal>
            <div className="space-y-5">
              <div className="flex gap-5 rounded-2xl border border-line bg-white p-6" data-testid="contact-card-phone">
                <Phone size={24} strokeWidth={1.4} className="shrink-0 text-gold" aria-hidden="true" />
                <div>
                  <p className={labelCls}>Phone</p>
                  <a href={TEL_HREF} data-testid="contact-phone-link" className="text-sm font-medium text-navy transition-colors hover:text-gold">
                    {PHONE_DISPLAY}
                  </a>
                </div>
              </div>

              <div className="flex gap-5 rounded-2xl border border-line bg-white p-6" data-testid="contact-card-email">
                <Mail size={24} strokeWidth={1.4} className="shrink-0 text-gold" aria-hidden="true" />
                <div>
                  <p className={labelCls}>Email</p>
                  <a href={MAILTO} data-testid="contact-email-link" className="break-all text-sm font-medium text-navy transition-colors hover:text-gold">
                    {EMAIL}
                  </a>
                </div>
              </div>

              <div className="flex gap-5 rounded-2xl border border-line bg-white p-6" data-testid="contact-card-office">
                <MapPin size={24} strokeWidth={1.4} className="shrink-0 text-gold" aria-hidden="true" />
                <div>
                  <p className={labelCls}>Office</p>
                  <p className="text-sm leading-relaxed text-navy">6185 Deltona Blvd, Spring Hill, FL 34606</p>
                </div>
              </div>

              <div className="flex gap-5 rounded-2xl border border-line bg-white p-6" data-testid="contact-card-consultation">
                <CalendarDays size={24} strokeWidth={1.4} className="shrink-0 text-gold" aria-hidden="true" />
                <div>
                  <p className={labelCls}>Book a Consultation</p>
                  <a
                    href={CALENDLY}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-calendly-link"
                    className="group inline-flex items-center gap-2 text-sm font-medium text-gold transition-colors hover:brightness-110"
                  >
                    Schedule a Consultation
                    <span aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-1">
                      →
                    </span>
                  </a>
                </div>
              </div>
            </div>

            <p className="mt-6 text-sm leading-relaxed text-ink" data-testid="fsbo-line">
              Selling your home on your own? Joe can help —{" "}
              <a href={MAILTO} className="font-medium text-gold underline underline-offset-2 transition-colors hover:brightness-110">
                reach out
              </a>
              .
            </p>
          </Reveal>

          <Reveal delay={120} className="rounded-3xl border border-line bg-white p-8 md:p-10" data-testid="contact-form-card">
            {sent ? (
              <div className="flex h-full flex-col items-center justify-center py-10 text-center" data-testid="contact-thank-you">
                <CheckCircle2 size={48} strokeWidth={1.25} className="text-gold" aria-hidden="true" />
                <h2 className="mt-6 font-serif text-3xl font-bold text-navy">Thank you!</h2>
                <p className="mt-4 max-w-sm text-sm leading-relaxed text-ink">
                  Your message has been sent to Joe and he&rsquo;ll be in touch shortly. For anything urgent, email{" "}
                  <a href={MAILTO} className="font-medium text-navy underline underline-offset-2">
                    {EMAIL}
                  </a>{" "}
                  or call{" "}
                  <a href={TEL_HREF} className="font-medium text-navy underline underline-offset-2">
                    {PHONE_DISPLAY}
                  </a>
                  .
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="cf-name" className={labelCls}>
                      Name
                    </label>
                    <input id="cf-name" required data-testid="contact-name" className={inputCls} value={form.name} onChange={set("name")} />
                  </div>
                  <div>
                    <label htmlFor="cf-phone" className={labelCls}>
                      Phone
                    </label>
                    <input id="cf-phone" type="tel" data-testid="contact-phone-field" className={inputCls} value={form.phone} onChange={set("phone")} />
                  </div>
                </div>
                <div className="mt-5">
                  <label htmlFor="cf-email" className={labelCls}>
                    Email
                  </label>
                  <input id="cf-email" type="email" required data-testid="contact-email-field" className={inputCls} value={form.email} onChange={set("email")} />
                </div>
                <div className="mt-5">
                  <label htmlFor="cf-interest" className={labelCls}>
                    I&rsquo;m interested in
                  </label>
                  <select id="cf-interest" required data-testid="contact-interest" className={inputCls} value={form.interest} onChange={set("interest")}>
                    <option value="" disabled>
                      Select an option
                    </option>
                    {INTERESTS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mt-5">
                  <label htmlFor="cf-message" className={labelCls}>
                    Message
                  </label>
                  <textarea id="cf-message" rows={5} data-testid="contact-message" className={inputCls} value={form.message} onChange={set("message")} />
                </div>
                <div className="mt-8">
                  {error && (
                    <p className="mb-4 text-sm leading-relaxed text-red-600" data-testid="contact-error">
                      {error}{" "}
                      <a href={MAILTO} className="font-medium underline underline-offset-2">
                        {EMAIL}
                      </a>
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="contact-submit"
                    className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-navy px-7 py-3.5 text-[12px] font-medium uppercase tracking-[0.12em] leading-none text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-navylight hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {busy ? "Sending…" : "Send Message"}
                    {!busy && (
                      <span aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-1">
                        →
                      </span>
                    )}
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
